/**
 * Line-of-sight (LOS) helpers.
 *
 * Cell-based: vision is computed in grid coordinates. For each candidate cell
 * within the PC's visibility radius, we walk a Bresenham line from the PC's
 * cell to the target. If any *intermediate* cell is opaque, the target is
 * occluded. Source cells are always considered visible (a PC standing on
 * a wall still sees the cell they occupy).
 *
 * All math is integer-only and DOM-free so we can unit-test it.
 */

import { cellKey, isOpaque } from './overlay.js';
import type { MapOverlay } from './types.js';

/**
 * Bresenham line between two integer points (inclusive of both endpoints).
 * Order is from (x0,y0) to (x1,y1). Used by hasLineOfSight to walk cells.
 */
export function bresenhamLine(x0: number, y0: number, x1: number, y1: number): { x: number; y: number }[] {
  const cells: { x: number; y: number }[] = [];
  let dx = Math.abs(x1 - x0);
  let dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  let x = x0;
  let y = y0;
  // Cap iterations defensively in case dx/dy are pathological.
  const maxSteps = dx + dy + 1;
  for (let i = 0; i <= maxSteps; i++) {
    cells.push({ x, y });
    if (x === x1 && y === y1) break;
    const e2 = err * 2;
    if (e2 > -dy) {
      err -= dy;
      x += sx;
    }
    if (e2 < dx) {
      err += dx;
      y += sy;
    }
  }
  return cells;
}

/**
 * True if there's an unobstructed line from (x0,y0) to (x1,y1).
 * Intermediate cells (exclusive of both endpoints) are checked for opacity.
 * Same-cell calls are always true.
 */
export function hasLineOfSight(
  x0: number, y0: number, x1: number, y1: number,
  overlay: MapOverlay | null,
): boolean {
  if (x0 === x1 && y0 === y1) return true;
  const cells = bresenhamLine(x0, y0, x1, y1);
  // Skip first (source) and last (target) — only intermediates block.
  for (let i = 1; i < cells.length - 1; i++) {
    const c = cells[i]!;
    if (isOpaque(overlay, c.x, c.y)) return false;
  }
  return true;
}

/**
 * Set of cell keys visible from (cx, cy) within `visibilityCells` radius.
 * Distance check is Euclidean (circle, not square). Each candidate cell is
 * then LOS-checked against the overlay.
 */
export function visibleCells(
  cx: number, cy: number, visibilityCells: number,
  overlay: MapOverlay | null,
): Set<string> {
  const result = new Set<string>();
  if (visibilityCells <= 0) {
    result.add(cellKey(cx, cy));
    return result;
  }
  const r = visibilityCells;
  const r2 = r * r;
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      if (dx * dx + dy * dy > r2) continue;
      const tx = cx + dx;
      const ty = cy + dy;
      if (hasLineOfSight(cx, cy, tx, ty, overlay)) {
        result.add(cellKey(tx, ty));
      }
    }
  }
  return result;
}
